import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { endpoints } from '@/config/apiEndpoints';
import { FIRESTORE_ENABLED } from '@/config/env';
import { useActiveOrganizationId } from '@/features/auth/session';
import { byName } from '@/features/brandHub/byName';
import { brandHubQueryKey } from '@/features/brandHub/queryKeys';
import { useLiveCollection, type LiveCollection } from '@/hooks/useLiveCollection';
import { apiClient } from '@/lib/apiClient';
import { collections, db } from '@/lib/firebase';
import { parseProduct, type Product } from './types';

const PRODUCT_LIMIT = 2000;

export function productCountQueryKey(organizationId: string | null, slug: string | null) {
  return brandHubQueryKey('products', organizationId, slug, 'count');
}

/** One brand's catalogue, streamed from the brand's own sub-collection and sorted by name here. */
export function useBrandProducts(slug: string | null): LiveCollection<Product> {
  const organizationId = useActiveOrganizationId();

  const collection = useLiveCollection<Product>({
    path: collections.products(organizationId ?? '_', slug ?? '_'),
    httpPath: endpoints.brandHub.product.list(slug ?? '_'),
    queryKey: brandHubQueryKey('products', organizationId, slug),
    parse: parseProduct,
    limit: PRODUCT_LIMIT,
    enabled: Boolean(organizationId && slug),
  });

  const data = useMemo(() => byName(collection.data, (product) => product.current.name), [collection.data]);
  return { ...collection, data };
}

interface ItemsResponse {
  items: ({ id: string } & Record<string, unknown>)[];
}

/** Counts one brand's products in one aggregate read, or off the HTTP list without Firestore. */
async function countBrandProducts(organizationId: string, slug: string): Promise<number> {
  if (!FIRESTORE_ENABLED) {
    const response = await apiClient.get<ItemsResponse>(endpoints.brandHub.product.list(slug));
    return response.items.length;
  }
  const { collection, getCountFromServer } = await import('firebase/firestore');
  const snapshot = await getCountFromServer(
    collection(await db(), collections.products(organizationId, slug)),
  );
  return snapshot.data().count;
}

/** How many products one brand holds, for a badge. One read, never the whole catalogue. */
export function useBrandProductCount(slug: string | null): number {
  const organizationId = useActiveOrganizationId();
  const query = useQuery({
    queryKey: productCountQueryKey(organizationId, slug),
    enabled: Boolean(organizationId && slug),
    queryFn: () => countBrandProducts(organizationId ?? '', slug ?? ''),
  });
  return query.data ?? 0;
}
